<template>
  <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-200 rounded-lg">

    <!-- Header -->
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center justify-between">
        <div>
          <h3 class="font-semibold text-base text-blueGray-700">Wishlist</h3>
          <p class="text-xs text-blueGray-400 mt-0.5">Items users have saved</p>
        </div>
        <span class="text-xs text-blueGray-400">
          {{ (wishlistItems().data || []).length }} item{{ (wishlistItems().data || []).length !== 1 ? 's' : '' }} saved
        </span>
      </div>
    </div>

    <div class="block w-full px-4 pb-6">

      <!-- Loading -->
      <div v-if="loaders().getWishlistItems" class="flex items-center justify-center py-12">
        <span class="loader block"></span>
      </div>

      <!-- Empty -->
      <div v-else-if="!(wishlistItems().data || []).length" class="flex flex-col items-center justify-center py-12 text-blueGray-300">
        <i class="fa fa-heart text-4xl mb-3"></i>
        <p class="text-sm font-semibold text-blueGray-400">No items saved yet</p>
        <p class="text-xs text-blueGray-300 mt-1">When users save products, they'll appear here.</p>
      </div>

      <!-- Wishlist cards -->
      <div v-else class="flex flex-wrap -mx-2">
        <div
          v-for="(item, i) in (wishlistItems().data || [])"
          :key="i"
          class="w-full md:w-6/12 px-2 mb-4"
        >
          <div class="rounded-lg border border-blueGray-200 overflow-hidden shadow-sm h-full">

            <!-- Item row -->
            <div class="flex items-center gap-4 px-4 py-3 bg-blueGray-50 border-b border-blueGray-200">
              <div class="w-10 h-10 rounded overflow-hidden bg-blueGray-100 flex-shrink-0">
                <img
                  v-if="item.thumbnails && item.thumbnails.length"
                  :src="item.thumbnails[0]"
                  class="w-full h-full object-cover"
                  :alt="item.name"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-blueGray-300">
                  <i class="fa fa-image"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <h6 class="text-sm font-semibold text-blueGray-700 truncate">{{ item.name }}</h6>
                <p v-if="item.sub_title" class="text-xs text-blueGray-400 truncate">{{ item.sub_title }}</p>
                <p class="text-xs text-blueGray-500 mt-1">
                  {{ formatter("NGN").format(item.price - (item.discount || 0)) }}
                  <span v-if="item.discount" class="line-through text-blueGray-300 ml-1">
                    {{ formatter("NGN").format(item.price) }}
                  </span>
                </p>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span class="inline-flex items-center gap-1 bg-red-50 text-red-500 text-xs font-bold px-3 py-1 rounded-full">
                  <i class="fa fa-heart text-xs"></i>
                  {{ item.customers_count || (item.customers || []).length }}
                </span>
                <button
                  @click="showInfo(item)"
                  class="text-blueGray-400 hover:text-emerald-600 transition-colors"
                  title="View details"
                >
                  <i class="fa fa-eye text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Likers list -->
            <div class="px-4 py-3">
              <p class="text-xs font-semibold text-blueGray-500 uppercase mb-2">Saved by</p>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(customer, j) in (item.customers || [])"
                  :key="j"
                  class="flex items-center gap-2 bg-blueGray-50 rounded-full pl-1 pr-3 py-1"
                >
                  <div class="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span class="text-emerald-700 font-bold text-xs uppercase">
                      {{ customer.name ? customer.name.charAt(0) : '?' }}
                    </span>
                  </div>
                  <div>
                    <p class="text-xs font-semibold text-blueGray-700 leading-none">{{ customer.name }}</p>
                    <p class="text-xs text-blueGray-400 leading-none mt-0.5">{{ customer.email }}</p>
                  </div>
                </div>
                <p v-if="!(item.customers || []).length" class="text-xs text-blueGray-400 italic">
                  No user details available.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <transition-group>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'wish-modal'">
        <div
          v-if="modals.info && selectedItem"
          @click.self="$_close_all_modal"
          style="width: 100%;"
          class="overflow-x-hidden overflow-y-auto fixed inset-0 z-40 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0"
        >
          <div class="relative w-auto z-50">
            <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                <h3 class="text-xl font-semibold capitalize">Item Details</h3>
                <button
                  class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  @click="toggleModal('info', false)"
                >
                  <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                </button>
              </div>
              <div class="relative p-6 flex-auto" style="max-height: 460px; overflow-y: auto;">
                <recursive-table
                  :data="selectedItem"
                  :exclude="['id', 'shop_id', 'category_id']"
                  :copies="['uuid']"
                  :dates="['created_at', 'updated_at']"
                  :images="['thumbnails']"
                />
              </div>
              <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                <button
                  class="text-red-500 background-transparent capitalize px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  @click="toggleModal('info', false)"
                >Close</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'wish-overlay'">
        <div v-if="modals.info" @click="$_close_all_modal" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-30 h-screen top-0 left-0 bg-black"></div>
      </transition>
    </transition-group>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatter } from '../../utils/formatter.util';
import { modal } from '../../mixins';
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';

export default {
  mixins: [modal],
  components: { RecursiveTable },
  data() {
    return {
      selectedItem: null,
      modals: { info: false },
    };
  },
  methods: {
    ...mapGetters(['wishlistItems', 'loaders']),
    ...mapActions(['getWishlistItems']),

    toggleModal(name, val) { this.modals[name] = val; },

    showInfo(item) {
      this.selectedItem = item;
      this.toggleModal('info', true);
    },
  },
  mounted() {
    this.getWishlistItems();
  },
  created() {
    this.formatter = formatter;
  },
};
</script>
